import { Button } from "@/components/ui/button";
import { useState } from "react";
import { ReviewModal } from "@/lib/builder/modals/ReviewModal";
import { ItineraryPaymentModal } from "@/lib/builder/modals/ItineraryPaymentModal";
import { Modal } from "@/components/ui/modal";
import { ChevronLeft, ChevronRight, MessageSquareDiff, CreditCard, Info, Mail } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useBuilderCapabilities } from "@/lib/builder/capabilities/BuilderCapabilitiesContext";
import { useI18n } from '@/hooks/useI18n';
import { usePaymentPlanQuery } from "@/lib/builder/hooks/usePaymentPlan";
import { summarizePlan } from "@/lib/builder/utils/payment-plan.utils";
import { TravelerPaymentSummary } from "@/lib/builder/components/payment-plan/TravelerPaymentSummary";

interface FooterButton {
  label: string;
  icon: JSX.Element | null;
  onClick: (() => void) | undefined;
  show: boolean;
  disabled?: boolean;
  className?: string;
}

// Ruolo, sola lettura e template non arrivano piu' come props: quali pulsanti mostrare
// lo dicono le capacita' del flusso (useBuilderCapabilities). Restano qui solo le
// condizioni di runtime che le capacita' non possono sapere: step corrente e piano rate.
interface BuilderFooterProps {
  isLoading?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isRecapStep?: boolean;
  canProceed?: boolean;
  /** Totale costi servizi, mostrato quando le capacita' lo prevedono (template). */
  servicesTotal?: number;
  totalPrice?: number;
  startDate?: string;
  endDate?: string;
  itineraryStatus?: string;
  onCancel?: () => void;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  onCreateQuote?: () => void;
  onRejectItinerary?: (note?: string) => Promise<void> | void;
  onAcceptItinerary?: (note?: string) => Promise<void> | void;
  onUpdateItineraryNote?: (note: string) => Promise<void>;
  onRequestChanges?: () => void;
  refundable?: 'full' | 'partial' | 'non_refundable' | null;
  /** Serve al viaggiatore per leggere il piano rate e aprire il pagamento. */
  itineraryId?: string | null;
  travelRequestId?: string | null;
  cancellationPolicyNotes?: string | null;
}

const formatDateRange = (startDate?: string, endDate?: string): string | null => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startMonth = start.toLocaleString('en-US', { month: 'short' });
  const endMonth = end.toLocaleString('en-US', { month: 'short' });
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

export const BuilderFooter = ({
  isLoading = false,
  isFirstStep = false,
  isLastStep = false,
  isRecapStep = false,
  canProceed = true,
  servicesTotal,
  totalPrice,
  startDate,
  endDate,
  itineraryStatus,
  onCancel,
  onPreviousStep,
  onNextStep,
  onCreateQuote,
  onRejectItinerary,
  onAcceptItinerary,
  onUpdateItineraryNote,
  onRequestChanges,
  refundable,
  itineraryId,
  travelRequestId,
  cancellationPolicyNotes,
}: BuilderFooterProps) => {

  const REFUNDABLE_CONFIG: Record<'full' | 'partial' | 'non_refundable', { label: string; description: string }> = {
    non_refundable: {
      label: 'Non refundable',
      description: 'No refunds will be issued for cancellations, changes, or no-shows.',
    },
    partial: {
      label: 'Partially refundable',
      description: 'A cancellation fee may apply; only part of the total amount may be refunded depending on when you cancel.',
    },
    full: {
      label: 'Refundable',
      description: 'This package is fully refundable according to the cancellation terms agreed at booking.',
    },
  };

  const refundableInfo = refundable ? REFUNDABLE_CONFIG[refundable] : null;
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { t } = useI18n();

  const capabilities = useBuilderCapabilities();
  const isTraveler = capabilities.audience === 'client';

  // Il piano rate serve solo al viaggiatore: chi vende lo vede nel checkout, con l'editor.
  // La query resta disabilitata senza id, quindi il ramo advisor non fa richieste.
  const { data: paymentPlan } = usePaymentPlanQuery(
    capabilities.canPay && itineraryId ? itineraryId : undefined
  );
  const planSummary = summarizePlan(paymentPlan);

  // "Saldato" non si legge dallo status quando c'e' un piano rate: mark_installment_paid
  // porta l'itinerario a 'completed' gia' al primo acconto (serve ad archiviare le proposte
  // fratelle), e fidarsi dello status nasconderebbe al cliente le rate ancora da versare.
  // Con un piano comanda il piano; senza piano vale lo status, come prima.
  const isSettled = planSummary ? planSummary.allPaid : itineraryStatus === 'completed';
  const canPay = capabilities.canPay && !!itineraryId && !isSettled && itineraryStatus !== 'request_changes';


  const leftButtons: FooterButton[] = [
    {
      label: "Request itinerary changes",
      icon: <MessageSquareDiff className="h-4 w-4" />,
      onClick: onRequestChanges,
      show: capabilities.canRequestChanges && !!onRequestChanges,
    },
    {
      label: "Exit",
      icon: <ChevronLeft className="h-4 w-4" />,
      onClick: onCancel,
      show: ((isTraveler && itineraryStatus === 'completed') || (!isTraveler && isFirstStep)) && !!onCancel,
    },
    {
      label: "Back",
      icon: <ChevronLeft className="h-4 w-4" />,
      onClick: onPreviousStep,
      show: !isTraveler && !isFirstStep && !!onPreviousStep,
    }
  ];

  const rightButtons: FooterButton[] = [
    {
      label: t('itinerary.payment.pay'),
      icon: <CreditCard className="h-4 w-4" />,
      onClick: () => setShowPaymentModal(true),
      show: canPay,
    },
    {
      label: t('itinerary.payment.paidBadge'),
      icon: (
        <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
      onClick: undefined,
      show: capabilities.canPay && isSettled,
      disabled: true,
      className: "rounded-2xl disabled:bg-green-600 px-3 py-2 readonly text-white font-semibold",
    },
    // {
    //   label: "Complete review",
    //   icon: null,
    //   onClick: () => setShowReviewModal(true),
    //   show: isAdmin && isLastStep && itineraryStatus === 'pending',
    // },
    {
      label: t("itinerary.sendToClient"),
      icon: <Mail className="h-4 w-4" />,
      onClick: onCreateQuote,
      show: isLastStep && capabilities.canSendToClient,
    },
    {
      label: "Next",
      icon: <ChevronRight className="h-4 w-4" />,
      onClick: onNextStep,
      show: !isLastStep && capabilities.hasCheckout,
    }
  ];

  if (isLoading) return null;
  if (isTraveler && itineraryStatus === 'request_changes') return null;

  return (
    <>
      <footer className="flex-shrink-0 border-t border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-2">
            {leftButtons.filter(b => b.show).map((b, idx) => (
              <Button
                key={b.label + idx}
                size="sm"
                onClick={b.onClick}
                className={`flex items-center gap-2 ${b.className || ''}`}
                variant={b.label === "Exit" || b.label === 'Back' ? "ghost" : b.label === "Request itinerary changes" ? "outline" : undefined}
                disabled={b.disabled || (b.label === "Next" && !canProceed)}
                tabIndex={b.disabled ? -1 : undefined}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {/* Sul template il checkout non esiste (nessuna richiesta collegata, nessuna rata):
                il totale dei costi servizi va mostrato qui, altrimenti non si legge da nessuna parte. */}
            {capabilities.showServicesTotalInFooter && !!servicesTotal && (
              <div className="text-right">
                <h4 className="text-gray-900">
                  € {servicesTotal.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h4>
                <span className="text-gray-500">{t('checkout.servicesCost')}</span>
              </div>
            )}
            {!!totalPrice && isTraveler && (itineraryStatus === 'sended' || itineraryStatus === 'completed') && (
              <div className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <h4 className="text-gray-900">
                    € {totalPrice.toLocaleString('it-IT')} total
                  </h4>
                  {cancellationPolicyNotes && (
                    <button
                      onClick={() => setShowPolicyModal(true)}
                      className="flex-shrink-0"
                      aria-label={t('sharedProposal.viewCancellationPolicy')}
                    >
                      <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                    </button>
                  )}
                </div>
                {/* Riassunto del pagamento: il prezzo da solo non dice se e' tutto dovuto
                    adesso o se c'e' un acconto gia' versato e un saldo in scadenza. */}
                <TravelerPaymentSummary plan={paymentPlan} isSettled={isSettled} />
                {refundableInfo && (
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <span className="text-gray-500">{refundableInfo.label}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-gray-400 cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-center">
                        {refundableInfo.description}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
            {rightButtons.filter(b => b.show).map((b, idx) => (
              <Button
                key={b.label + idx}
                size="sm"
                onClick={b.onClick}
                className={`flex items-center gap-2 ${b.className || ''}`}
                variant={b.label === "Exit" || b.label === 'Back' ? "ghost" : b.label === "Request itinerary changes" ? "outline" : undefined}
                disabled={b.disabled || (b.label === "Next" && !canProceed)}
                tabIndex={b.disabled ? -1 : undefined}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}
          </div>
        </div>
      </footer>
      {showReviewModal && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onAccept={onAcceptItinerary}
          onReject={onRejectItinerary}
        />
      )}
      <Modal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        title={t('sharedProposal.cancellationPolicy')}
        size="sm"
      >
        <div className="px-6 py-4 text-sm text-gray-700 whitespace-pre-wrap">
          {cancellationPolicyNotes}
        </div>
      </Modal>
      {isTraveler && itineraryId && (
        <ItineraryPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itineraryId={itineraryId}
          itineraryTotal={totalPrice}
          isSettled={isSettled}
        />
      )}
    </>
  );
};
